import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Lightbulb,
  Upload,
  Wrench,
  Target,
  Clock,
  FlaskConical,
  Copy,
} from 'lucide-react';
import { useStrategies, useCreateStrategy } from '@/hooks/use-strategies';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { formatPct, timeAgo } from '@/lib/format';
import type { StrategyTemplate } from '@/types/api';

type Filter = 'all' | 'published' | 'draft' | 'archived';

export default function StrategyListPage() {
  const { data: strategies, isLoading, error } = useStrategies();
  const [filter, setFilter] = useState<Filter>('all');
  const [duplicateTarget, setDuplicateTarget] = useState<StrategyTemplate | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const createStrategy = useCreateStrategy();
  const navigate = useNavigate();

  function openDuplicateModal(strategy: StrategyTemplate) {
    setDuplicateTarget(strategy);
    setDuplicateName(`${strategy.name} (copy)`);
  }

  async function confirmDuplicate() {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      const result = await createStrategy.mutateAsync({
        name: duplicateName.trim() || `${duplicateTarget.name} (copy)`,
        symbol: duplicateTarget.symbol || '',
        description: duplicateTarget.description || '',
        strategy_type: (duplicateTarget as any).strategy_type || 'CONDITION_ENGINE',
        rules: duplicateTarget.rules,
      });
      setDuplicateTarget(null);
      navigate(`/strategies/${result.id}`);
    } catch {
      // mutation error handled by React Query
    } finally {
      setDuplicating(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load strategies</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const all = strategies ?? [];
  const filtered =
    filter === 'published'
      ? all.filter((s) => s.latest_version > 0 && s.status !== 'ARCHIVED')
      : filter === 'draft'
        ? all.filter((s) => s.status === 'DRAFT' && s.latest_version === 0)
        : filter === 'archived'
          ? all.filter((s) => s.status === 'ARCHIVED')
          : all.filter((s) => s.status !== 'ARCHIVED');

  filtered.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const counts = {
    all: all.filter((s) => s.status !== 'ARCHIVED').length,
    published: all.filter((s) => s.latest_version > 0 && s.status !== 'ARCHIVED').length,
    draft: all.filter((s) => s.status === 'DRAFT' && s.latest_version === 0).length,
    archived: all.filter((s) => s.status === 'ARCHIVED').length,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Strategies</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            {all.length} strateg{all.length !== 1 ? 'ies' : 'y'} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/strategies/new">
            <Button size="sm">
              <Wrench size={14} />
              Build
            </Button>
          </Link>
          <Link to="/strategies/new/upload">
            <Button variant="ghost" size="sm">
              <Upload size={14} />
              Upload
            </Button>
          </Link>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2">
        {(['all', 'published', 'draft', 'archived'] as const).map((f) => {
          const label = f === 'all' ? 'All' : f === 'published' ? 'Published' : f === 'draft' ? 'Drafts' : 'Archived';
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                filter === f
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2 hover:text-ink'
              }`}
            >
              {label}
              <span className="ml-1.5 opacity-60">{counts[f]}</span>
            </button>
          );
        })}
      </div>

      {/* Strategy List */}
      {filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="space-y-2">
          {filtered.map((strategy) => (
            <StrategyCard
              key={strategy.id}
              strategy={strategy}
              onDuplicate={openDuplicateModal}
              isDuplicating={duplicating && duplicateTarget?.id === strategy.id}
            />
          ))}
        </div>
      )}

      {/* Duplicate confirmation modal */}
      <Modal
        open={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        title="Duplicate Strategy"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDuplicateTarget(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={confirmDuplicate} disabled={duplicating}>
              {duplicating ? <Spinner size="sm" /> : <Copy size={12} />}
              Duplicate
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-ink-3">
            This will create a new draft strategy with the same rules and configuration.
          </p>
          <div>
            <label className="block text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1.5">
              Strategy Name
            </label>
            <input
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              placeholder={duplicateTarget?.name ? `${duplicateTarget.name} (copy)` : ''}
              className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-xs text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────

function StrategyCard({ strategy, onDuplicate, isDuplicating }: { strategy: StrategyTemplate; onDuplicate: (s: StrategyTemplate) => void; isDuplicating: boolean }) {
  const snapshot = strategy.analysis_snapshot as Record<string, unknown> | null;
  const stats = (snapshot?.stats ?? {}) as Record<string, unknown>;
  const breakdown = (snapshot?.strategy_breakdown ?? {}) as Record<string, unknown>;
  const riskProfile = (breakdown?.risk_profile ?? {}) as Record<string, unknown>;
  const rules = strategy.rules as Record<string, unknown> | null;
  const marketData = (rules?.market_data ?? {}) as Record<string, unknown>;

  // Version + date
  const version = strategy.latest_version;
  const updatedAt = strategy.updated_at;

  // Uploaded trade metrics
  const hasRealStats = typeof stats.total_pnl === 'number';
  const tradeCount = typeof stats.trade_count === 'number' ? stats.trade_count : null;
  const winRate = typeof stats.win_rate === 'number' ? stats.win_rate : null;
  const roi = hasRealStats && typeof stats.roi === 'number' ? stats.roi : null;
  const maxDd = typeof stats.drawdown === 'number' ? stats.drawdown : null;
  const duration = typeof stats.duration === 'number' ? stats.duration : null;

  // Metadata
  const inferredType = typeof snapshot?.inferred_strategy_type === 'string' ? snapshot.inferred_strategy_type : null;
  const symbol = strategy.symbol || (typeof stats.symbol === 'string' ? stats.symbol : null);
  const timeframe = typeof marketData.timeframe === 'string' ? marketData.timeframe : null;
  const riskLevel = typeof riskProfile.level === 'string' ? riskProfile.level : null;

  // Custom-built config summary
  const entryRules = (rules?.entry as any)?.rules;
  const exitRules = (rules?.exit as any)?.rules;
  const lotSizingType = (rules?.risk as any)?.lotSizing?.type;

  const isDraftWithHistory = strategy.status === 'DRAFT' && version > 0;
  const statusLabel = isDraftWithHistory ? `V${version + 1} Draft` : strategy.status;
  const statusColor = strategy.status === 'PUBLISHED' ? 'green' : strategy.status === 'DRAFT' ? 'amber' : strategy.status === 'ARCHIVED' ? 'red' : 'default';
  const iconColor = strategy.status === 'PUBLISHED' ? 'text-green' : strategy.status === 'DRAFT' ? 'text-amber' : strategy.status === 'ARCHIVED' ? 'text-ink-4' : 'text-ink-4';

  const typeLabel = inferredType
    ? inferredType.includes('GRID')
      ? 'GRID'
      : inferredType.includes('TREND')
        ? 'TREND'
        : 'CE'
    : null;

  const entryCount = Array.isArray(entryRules) ? entryRules.length : 0;
  const exitCount = Array.isArray(exitRules) ? exitRules.length : 0;
  const lotLabel = lotSizingType
    ? lotSizingType.charAt(0).toUpperCase() + lotSizingType.slice(1).toLowerCase().replace(/_/g, '-')
    : null;

  return (
    <Link
      to={`/strategies/${strategy.id}`}
      className="border border-glass-border rounded-lg glass-sm p-4 flex items-start gap-3 hover:border-orange/15 transition-all group"
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Lightbulb size={18} className={iconColor} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Name + timestamp */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm font-bold truncate group-hover:text-orange transition-colors">
            {strategy.name}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {updatedAt && (
              <span className="text-[10px] text-ink-4 flex items-center gap-1">
                <Clock size={10} />
                {timeAgo(updatedAt)}
              </span>
            )}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate(strategy);
              }}
              disabled={isDuplicating}
              className="text-ink-4 hover:text-orange transition-colors p-1 rounded hover:bg-orange/10 disabled:opacity-50 cursor-pointer"
              title="Duplicate strategy"
            >
              <Copy size={12} />
            </button>
          </div>
        </div>

        {/* Row 2: Badges */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <Badge color={statusColor}>{statusLabel}</Badge>
          {version > 0 && strategy.status === 'PUBLISHED' && (
            <span className="text-[10px] font-semibold text-ink-4 bg-surface-2 px-1.5 py-0.5 rounded">
              v{version}
            </span>
          )}
          {symbol && (
            <span className="text-[10px] font-mono font-semibold text-orange/70 bg-orange-bg px-1.5 py-0.5 rounded">
              {symbol}
            </span>
          )}
        </div>

        {/* Row 3: Metadata line */}
        {hasRealStats ? (
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {timeframe && <span className="text-[10px] text-ink-4">{timeframe}</span>}
            {timeframe && typeLabel && <span className="text-[10px] text-ink-4">·</span>}
            {typeLabel && <span className="text-[10px] text-ink-4">{typeLabel}</span>}
            {(timeframe || typeLabel) && riskLevel && <span className="text-[10px] text-ink-4">·</span>}
            {riskLevel && <span className="text-[10px] text-ink-4">{riskLevel}</span>}
            {winRate != null && (
              <>
                {(timeframe || typeLabel || riskLevel) && <span className="text-[10px] text-ink-4">·</span>}
                <span className="text-[10px] text-ink-4 flex items-center gap-1">
                  <Target size={10} />
                  {winRate.toFixed(0)}% WR
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-[10px] text-ink-4">CE</span>
            {entryCount > 0 && (
              <>
                <span className="text-[10px] text-ink-4">·</span>
                <span className="text-[10px] text-ink-4">
                  {entryCount} entr{entryCount === 1 ? 'y' : 'ies'}
                </span>
              </>
            )}
            {exitCount > 0 && (
              <>
                <span className="text-[10px] text-ink-4">·</span>
                <span className="text-[10px] text-ink-4">
                  {exitCount} exit{exitCount === 1 ? '' : 's'}
                </span>
              </>
            )}
            {lotLabel && (
              <>
                <span className="text-[10px] text-ink-4">·</span>
                <span className="text-[10px] text-ink-4">{lotLabel}</span>
              </>
            )}
          </div>
        )}

        {/* Row 4: Uploaded trade metrics strip */}
        {hasRealStats && (
          <div className="flex items-center gap-3 flex-wrap">
            {winRate != null && (
              <span className="text-[10px] font-mono text-ink-3">
                {winRate.toFixed(0)}% WR
              </span>
            )}
            {roi != null && (
              <span className={`text-[10px] font-mono font-semibold ${roi >= 0 ? 'text-green' : 'text-red'}`}>
                {formatPct(roi)} ROI
              </span>
            )}
            {maxDd != null && (
              <span className="text-[10px] font-mono text-amber">
                {maxDd.toFixed(1)}% DD
              </span>
            )}
            {duration != null && (
              <span className="text-[10px] font-mono text-ink-4">
                {duration.toFixed(1)}d
              </span>
            )}
            {tradeCount != null && (
              <span className="text-[10px] font-mono text-ink-3">
                {tradeCount} trades
              </span>
            )}
          </div>
        )}

        {/* Row 5: Backtest metrics strip */}
        {strategy.latest_backtest && (
          <div className="flex items-center gap-3 flex-wrap mt-0.5">
            <span className="text-[10px] font-semibold text-purple flex items-center gap-1">
              <FlaskConical size={10} />
              Backtest
            </span>
            <span className={`text-[10px] font-mono font-semibold ${(strategy.latest_backtest.returnPct ?? 0) >= 0 ? 'text-green' : 'text-red'}`}>
              {formatPct(strategy.latest_backtest.returnPct ?? 0)} ROI
            </span>
            <span className="text-[10px] font-mono text-ink-3">
              {(strategy.latest_backtest.winRate ?? 0).toFixed(0)}% WR
            </span>
            <span className="text-[10px] font-mono text-amber">
              {(strategy.latest_backtest.maxDrawdown ?? 0).toFixed(1)}% DD
            </span>
            <span className="text-[10px] font-mono text-ink-3">
              {strategy.latest_backtest.totalTrades ?? 0} trades
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

function EmptyState({ filter }: { filter: Filter }) {
  return (
    <div className="glass-sm rounded-lg p-10 text-center">
      <Lightbulb size={40} className="text-orange/40 mx-auto mb-3" />
      <h3 className="text-sm font-bold mb-1">
        {filter === 'all'
          ? 'No strategies yet'
          : filter === 'published'
            ? 'No published strategies'
            : 'No draft strategies'}
      </h3>
      <p className="text-xs text-ink-3 mb-4">
        {filter === 'all'
          ? 'Upload trade history to extract a strategy or build one from scratch.'
          : filter === 'published'
            ? 'Publish a draft strategy to see it here.'
            : 'Upload history or build a strategy to get started.'}
      </p>
      {filter === 'all' && (
        <div className="flex items-center justify-center gap-3">
          <Link to="/strategies/new/upload">
            <Button size="sm">
              <Upload size={14} />
              Upload History
            </Button>
          </Link>
          <Link to="/strategies/new">
            <Button variant="ghost" size="sm">
              <Wrench size={14} />
              Build Strategy
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
